import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";

import { type Filters, type Insight } from "../types";

export type FilterConfig = {
  title: string;
  items: string[];
  filterKey: keyof Filters;
  getLabel: (item: string) => string;
  filterFunction: (insight: Insight, item: string) => boolean;
  isExpandedByDefault: boolean;
};

type FilterSectionProps = {
  config: FilterConfig;
  filters: Filters;
  expandedFilters: Record<string, boolean>;
  insights: Insight[] | undefined;
  isExpanded: boolean;
  onFilterChange: (category: keyof Filters, value: string) => void;
  onExpandChange: (title: string, isExpanded: boolean) => void;
};

export const FilterSection: React.FC<FilterSectionProps> = ({
  config,
  filters,
  insights,
  isExpanded,
  onFilterChange,
  onExpandChange,
}) => {
  const { title, items, filterKey, getLabel, filterFunction } = config;
  const currentFilterValues = filters[filterKey].map(String);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Box key={title}>
      <Accordion
        disableGutters
        elevation={0}
        expanded={isExpanded}
        onChange={(_, newIsExpanded) => {
          onExpandChange(title, newIsExpanded);
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-cy={`accordion-${title}`}>
          <Typography variant="subtitle2">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {items.map((item) => (
              <FormControlLabel
                key={item}
                control={
                  <Checkbox
                    data-cy={getLabel(item)}
                    size="small"
                    checked={currentFilterValues.includes(item)}
                    onChange={() => {
                      onFilterChange(filterKey, item);
                    }}
                  />
                }
                label={
                  <Typography variant="body2" component="span">
                    {getLabel(item)}{" "}
                    <Typography variant="body2" component="span" color="textSecondary">
                      ({insights?.filter((insight) => filterFunction(insight, item)).length ?? 0})
                    </Typography>
                  </Typography>
                }
                sx={{ mb: 1 }}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
